import React, { useContext, useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    Stack,
    Chip,
    Divider,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import VentasService from "../../api/ventas.api";
import { StateContext } from "../../../../components/context/StateProvider";
import { BADGE_COLORS, getBadgeStyles } from "../../../../components/ui/badgeColors";
import { getErrorMessage } from "../../../../utils/ErrorUtils";
import Utils from "../../../../utils/Utils";
import { ESTADO_PROCESO } from "../../../../config/constants";

const LIMITE_PREPARADOS = 50;

/**
 * Últimos pedidos de Nave 2 ya preparados. Cada número abre el pedido, para consultarlo
 * o volver a imprimir su ticket
 */
const ModalPedidosPreparadosN2 = ({ show, onHide }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { state } = useContext(StateContext);

    const [pedidos, setPedidos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const cargar = async () => {
        setLoading(true);
        setError(null);
        try {
            setPedidos(await VentasService.getPreparadosN2(state.year, LIMITE_PREPARADOS));
        } catch (e) {
            setError(getErrorMessage(e, "No se pudieron cargar los pedidos preparados"));
        } finally {
            setLoading(false);
        }
    };

    // Se consulta cada vez que se abre: entre una apertura y otra se preparan pedidos
    useEffect(() => {
        if (show) {
            cargar();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const renderContenido = () => {
        if (loading) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress sx={{ color: "#cc071e" }} />
                </Box>
            );
        }

        if (error) {
            return <Alert severity="error">{error}</Alert>;
        }

        if (pedidos.length === 0) {
            return (
                <Typography sx={{ color: "#6c757d", textAlign: "center", py: 4, fontSize: 14 }}>
                    No hay pedidos preparados recientemente en nave 2
                </Typography>
            );
        }

        return (
            <Stack spacing={1.5}>
                {pedidos.map((pedido) => {
                    const tipo = (pedido.tipo || "").toLowerCase();
                    return (
                        <Box
                            key={`${pedido.tipo}-${pedido.numero}`}
                            sx={{ p: 1.5, border: "1px solid #e9ecef", borderRadius: 2, backgroundColor: "#fff" }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box
                                        component="a"
                                        href={`#/pedidosn2/order/${pedido.numero}?tipo=${encodeURIComponent(tipo)}&ruta=${encodeURIComponent(pedido.ruta || "")}`}
                                        sx={{ color: "#cc071e", fontWeight: 700, fontSize: 15, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                                    >
                                        {pedido.numero}
                                    </Box>
                                    <Chip
                                        label={Utils.toCapitalize(tipo)}
                                        size="small"
                                        sx={getBadgeStyles(BADGE_COLORS.GRAY, { height: 20, fontSize: 10 })}
                                    />
                                </Stack>
                                <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                                    {pedido.registrado ? Utils.formatDate(new Date(pedido.registrado)) : "-"}
                                </Typography>
                            </Stack>

                            {pedido.cliente && (
                                <Typography sx={{ fontSize: 13, color: "#212529", mt: 0.75 }}>
                                    {Utils.toCapitalize(pedido.cliente)}
                                </Typography>
                            )}

                            <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                <Chip
                                    label={`Prep. N2: ${pedido.preparado ? Utils.toCapitalize(pedido.preparado) : ESTADO_PROCESO.SIN_PREPARADOR}`}
                                    size="small"
                                    sx={getBadgeStyles(BADGE_COLORS.INDIGO, { height: 20, fontSize: 10 })}
                                />
                                {pedido.ruta && (
                                    <Chip
                                        label={Utils.toCapitalize(pedido.ruta)}
                                        size="small"
                                        sx={getBadgeStyles(BADGE_COLORS.BLUE, { height: 20, fontSize: 10 })}
                                    />
                                )}
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
        );
    };

    return (
        <Dialog
            open={show}
            onClose={onHide}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    m: isMobile ? 0 : 2
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                        }}
                    >
                        <HistoryIcon sx={{ color: "#cc071e", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: "#212529", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                        {isMobile ? "Preparados N2" : "Últimos pedidos preparados en nave 2"}
                    </Typography>
                </Box>
                <IconButton onClick={onHide} size="small">
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : "initial",
                    backgroundColor: "#f8f9fa"
                }}
            >
                {renderContenido()}
            </DialogContent>

            <Divider />

            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column-reverse", sm: "row" }
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onHide}
                    fullWidth={isMobile}
                    sx={{ textTransform: "none", fontWeight: 600, px: { xs: 2, sm: 2.5 }, py: { xs: 1.25, sm: 1 }, fontSize: { xs: 14, sm: 15 }, color: "#6c757d", borderColor: "#6c757d" }}
                >
                    Cerrar
                </Button>
                <Button
                    variant="contained"
                    onClick={cargar}
                    fullWidth={isMobile}
                    disabled={loading}
                    startIcon={<RefreshIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        backgroundColor: "#cc071e",
                        "&:hover": { backgroundColor: "#a30618" },
                        "&:disabled": { backgroundColor: "#6c757d", color: "#fff" }
                    }}
                >
                    Actualizar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalPedidosPreparadosN2;
